import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { _Cart_Update } from "../store/actions/cart_actions";
import { set_checkout_id, set_payment_options } from "../store/actions/login_actions";
import { CartItemTransformer } from "../services/objectTransformer/cart";
import { calculator, showAllErrors } from "../base/utils";
import { Note } from "../helbest";

import { useCreateCheckoutMutation, usePaymentGatewayInitializeMutation } from "../saleor/api";

export const useAutoCheckoutCreate = ({
  enabled,
  variantId,
  quantity,
  channel,
  cid,
  cpid,
  language,
  checkoutRefetch,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [hasCreated, setHasCreated] = useState(false);
  const [noGatewaysAvailable, setNoGatewaysAvailable] = useState(false);

  const [createCheckout] = useCreateCheckoutMutation();
  const [initPayment] = usePaymentGatewayInitializeMutation();

  useEffect(() => {
    if (!enabled || hasCreated) return;

    setHasCreated(true);
    setLoading(true);

    const createFlow = async () => {
      try {
        // 1️⃣ Create checkout
        const { data: createData } = await createCheckout({
          variables: {
            input: {
              channel,
              lines: [
                {
                  variantId,
                  quantity,
                  metadata: [
                    { key: "cid", value: cid || "" },
                    { key: "cpid", value: cpid },
                    { key: "channel", value: channel },
                    { key: "language", value: language },
                  ],
                },
              ],
              languageCode: language,
            },
          },
        });

        const checkout = createData.checkoutCreate.checkout;

        if (createData.checkoutCreate.errors.length) {
          showAllErrors(createData.checkoutCreate.errors);
          setHasCreated(false);
          setLoading(false);
          return;
        }

        dispatch(set_checkout_id({ checkout_id: checkout.id }));
        dispatch(_Cart_Update(checkout.lines.map(line => CartItemTransformer(line))));
        calculator();

        // 2️⃣ Refetch checkout to get full data
        const { data: refetchedData } = await checkoutRefetch?.();
        const updatedCheckout = refetchedData?.checkout;

        if (!updatedCheckout) {
          throw new Error("Checkout refetch failed");
        }

        // 3️⃣ Initialize payment
        const gateways = updatedCheckout.availablePaymentGateways;
        if (gateways?.length) {
          const { data: paymentData } = await initPayment({
            variables: {
              id: updatedCheckout.id,
              paymentGateways: gateways.map(g => ({ id: g.id })),
            },
          });

          if (paymentData?.paymentGatewayInitialize?.errors?.length) {
            showAllErrors(paymentData.paymentGatewayInitialize.errors);
          } else {
            dispatch(set_payment_options(paymentData.paymentGatewayInitialize.gatewayConfigs));
          }
        } else {
          console.warn("No available payment gateways, skipping initPayment");
          setNoGatewaysAvailable(true);
          dispatch(set_payment_options([]));
        }

        // 4️⃣ Final checkout refetch
        await checkoutRefetch?.();

        // Cleanup pendingFastChackout
        localStorage.removeItem("pendingFastChackout");

        setLoading(false);
      } catch (err) {
        console.error("❌ Checkout flow failed", err);
        Note.error("Checkout flow failed");
        setHasCreated(false);
        setLoading(false);
      }
    };

    createFlow();
  }, [
    enabled,
    hasCreated,
    variantId,
    quantity,
    channel,
    cid,
    cpid,
    language,
    checkoutRefetch,
    createCheckout,
    initPayment,
    dispatch,
  ]);

  return { loading, noGatewaysAvailable };
};
